import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { onAnimationEnd } from "@theme/utilities";

const ALHENA_STICKY_BAR_GAP = 16;
const ALHENA_DEFAULT_MOBILE_BOTTOM_SPACING = 0;
const ALHENA_LAUNCHER_ANIMATION_DURATION = 250;

/** @typedef {import('@theme/events').QuantitySelectorUpdateEvent} QuantitySelectorUpdateEvent */

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} GleenWidget
 * @property {(styles: { mobile_bottom_spacing: number }) => void} [setStyles]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * On desktop, shows whenever the inline buy buttons are not fully in view.
 * On mobile, shows whenever the inline Add to Bag button is fully outside
 * the viewport, and hides again while that button is on screen.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    "stickyBar",
    "addToCartButton",
    "quantityDisplay",
    "quantityNumber",
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | null} */
  #alhenaAnimationFrame = null;

  /** @type {number} */
  #alhenaMobileBottomSpacing = ALHENA_DEFAULT_MOBILE_BOTTOM_SPACING;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #buyButtonsFullyVisible = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section");
    target?.addEventListener(
      ThemeEvents.variantUpdate,
      /** @type {EventListener} */ (this.#handleVariantUpdate),
      { signal },
    );
    target?.addEventListener(
      ThemeEvents.variantSelected,
      /** @type {EventListener} */ (this.#handleVariantSelected),
      { signal },
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      /** @type {EventListener} */ (this.#handleCartAddComplete),
      { signal },
    );
    document.addEventListener(
      ThemeEvents.cartError,
      /** @type {EventListener} */ (this.#handleCartAddComplete),
      { signal },
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      /** @type {EventListener} */ (this.#handleQuantityUpdate),
      { signal },
    );

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelAlhenaLauncherAnimation();
    this.#setAlhenaMobileBottomSpacing(ALHENA_DEFAULT_MOBILE_BOTTOM_SPACING);
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility.
   * Desktop keeps the bar up whenever the buy block is not fully in view.
   * Mobile follows the inline Add to Bag button, including while it sits
   * below the fold, so that check also runs on scroll.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest(".buy-buttons-block");
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer =
      document.querySelector("footer") ??
      document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#targetAddToCartButton = this.#getInlineAddToCartButton(productForm);
    this.#buyButtonsFullyVisible = this.#isFullyInViewport(buyButtonsBlock);
    this.#syncStickyVisibility();

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        this.#buyButtonsFullyVisible =
          entry.isIntersecting && entry.intersectionRatio >= 0.99;
        this.#syncStickyVisibility();
      },
      { threshold: [0, 0.99, 1] },
    );

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        this.#hiddenByBottom = entry.isIntersecting;
        this.#syncStickyVisibility();
      },
      {
        rootMargin: "200px 0px 0px 0px",
      },
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);

    const { signal } = this.#abortController;
    window
      .matchMedia("(max-width: 749px)")
      .addEventListener("change", this.#syncStickyVisibility, { signal });
    window.addEventListener("scroll", this.#syncStickyVisibility, {
      signal,
      passive: true,
    });
    window.addEventListener("resize", this.#syncStickyVisibility, { signal });
  }

  /**
   * The inline Add to Bag control inside the product form.
   * Skips a zero-height node and the sticky bar's own button.
   * @param {Element} productForm
   * @returns {HTMLButtonElement | null}
   */
  #getInlineAddToCartButton(productForm) {
    const block = productForm.closest(".buy-buttons-block") ?? productForm;
    const buttons = block.querySelectorAll("button.add-to-cart-button");
    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      if (button.closest("sticky-add-to-cart")) continue;
      if (button.getBoundingClientRect().height > 20) return button;
    }

    const refButton = block.querySelector('[ref="addToCartButton"]');
    return refButton instanceof HTMLButtonElement ? refButton : null;
  }

  /**
   * True when the inline Add to Bag button is fully above or below the viewport.
   * @returns {boolean}
   */
  #inlineButtonOffScreen() {
    const button = this.#targetAddToCartButton;
    if (!button) return false;

    const rect = button.getBoundingClientRect();
    if (rect.height < 20) return false;
    return rect.bottom <= 0 || rect.top >= window.innerHeight;
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  #isFullyInViewport(element) {
    const rect = element.getBoundingClientRect();
    if (rect.height < 20) return false;
    return rect.top >= 0 && rect.bottom <= window.innerHeight;
  }

  /**
   * Desktop: show while the buy block is not fully on screen.
   * Mobile: show while the inline Add to Bag button is fully off screen.
   */
  #syncStickyVisibility = () => {
    const mobile = window.matchMedia("(max-width: 749px)").matches;
    const buyControlMissing = mobile
      ? this.#inlineButtonOffScreen()
      : !this.#buyButtonsFullyVisible;
    const shouldShow = !this.#hiddenByBottom && buyControlMissing;
    if (shouldShow && !this.#isStuck) {
      this.#showStickyBar();
    } else if (!shouldShow && this.#isStuck) {
      this.#hideStickyBar();
    }
  };

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = "true";
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    if (this.refs.addToCartButton.dataset.added !== "true") {
      this.refs.addToCartButton.dataset.added = "true";
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage)
      return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );
    flyToCartElement.classList.add("fly-to-cart--sticky");
    flyToCartElement.style.setProperty(
      "background-image",
      `url(${this.refs.productImage.src})`,
    );
    flyToCartElement.useSourceSize = "true";
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute("data-added");
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart =
      event.detail.data.html.querySelector("sticky-add-to-cart");
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck =
      this.refs.stickyBar.getAttribute("data-stuck") || "false";
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute("data-stuck", currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    this.#syncAlhenaLauncherPosition();

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector(
        '[ref="addToCartButton"]',
      );
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(
      ".sticky-add-to-cart__variant",
    );
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`,
    );
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll("input:checked"),
    )
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== "")
      .join(" / ");
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = "false";
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = "true";
    this.#syncAlhenaLauncherPosition();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = "false";
    this.#syncAlhenaLauncherPosition();
  }

  // Helper methods
  /**
   * Keeps Alhena's floating launcher clear of the mobile sticky bar.
   */
  #syncAlhenaLauncherPosition() {
    if (!this.#isStuck) {
      this.#animateAlhenaMobileBottomSpacing(
        ALHENA_DEFAULT_MOBILE_BOTTOM_SPACING,
      );
      return;
    }

    const stickyBarHeight = this.refs.stickyBar.getBoundingClientRect().height;
    this.#animateAlhenaMobileBottomSpacing(
      Math.ceil(stickyBarHeight + ALHENA_STICKY_BAR_GAP),
    );
  }

  /**
   * @param {number} targetBottomSpacing
   */
  #animateAlhenaMobileBottomSpacing(targetBottomSpacing) {
    const alhenaWindow = /** @type {Window & { gleenWidget?: GleenWidget }} */ (
      window
    );
    const setStyles = alhenaWindow.gleenWidget?.setStyles;
    if (typeof setStyles !== "function") return;

    this.#cancelAlhenaLauncherAnimation();

    const startBottomSpacing = this.#alhenaMobileBottomSpacing;
    const spacingDelta = targetBottomSpacing - startBottomSpacing;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (spacingDelta === 0 || prefersReducedMotion) {
      this.#setAlhenaMobileBottomSpacing(targetBottomSpacing);
      return;
    }

    const startTime = performance.now();

    const animate = (/** @type {number} */ currentTime) => {
      const progress = Math.min(
        (currentTime - startTime) / ALHENA_LAUNCHER_ANIMATION_DURATION,
        1,
      );
      const easedProgress = 1 - (1 - progress) ** 3;
      const nextBottomSpacing = Math.round(
        startBottomSpacing + spacingDelta * easedProgress,
      );

      this.#setAlhenaMobileBottomSpacing(nextBottomSpacing);

      if (progress < 1) {
        this.#alhenaAnimationFrame = requestAnimationFrame(animate);
      } else {
        this.#alhenaAnimationFrame = null;
        this.#setAlhenaMobileBottomSpacing(targetBottomSpacing);
      }
    };

    this.#alhenaAnimationFrame = requestAnimationFrame(animate);
  }

  #cancelAlhenaLauncherAnimation() {
    if (this.#alhenaAnimationFrame === null) return;

    cancelAnimationFrame(this.#alhenaAnimationFrame);
    this.#alhenaAnimationFrame = null;
  }

  /**
   * @param {number} bottomSpacing
   */
  #setAlhenaMobileBottomSpacing(bottomSpacing) {
    const alhenaWindow = /** @type {Window & { gleenWidget?: GleenWidget }} */ (
      window
    );
    const setStyles = alhenaWindow.gleenWidget?.setStyles;
    if (typeof setStyles !== "function") return;

    setStyles.call(alhenaWindow.gleenWidget, {
      mobile_bottom_spacing: bottomSpacing,
    });
    this.#alhenaMobileBottomSpacing = bottomSpacing;
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace("shopify-section-", "");
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || "1") || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = "inline";
    } else {
      quantityDisplay.style.display = "none";
    }
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCartComponent);
}
